const DEFAULT_SIZE = 128;

/**
 * Music glyphs an avatar can show, one picked per uid. In order: eighth / beamed
 * eighths / quarter / beamed sixteenths note, treble clef (U+1D11E), quarter rest
 * (U+1D13D). The U+1D1xx symbols are in the SMP Musical Symbols block, drawn via
 * the system font's coverage like the rest.
 */
const GLYPHS = [
  "♪",
  "♫",
  "♩",
  "♬",
  "\u{1D11E}",
  "\u{1D13D}",
];

const GLYPH_FRAC = 0.62;
const GLYPH_CY_FRAC = 0.49;
const BG_TINT_ALPHA = 0.3;

/** FNV-1a over the uid string, giving a stable 32-bit seed for deterministic generation. */
function hashUid(uid: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < uid.length; i++) {
    h ^= uid.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Zero-padded 4-digit suffix derived from `hashUid`, so the same uid always shows the same digits across every client's anonymous-name display. */
export function anonymousIdSuffix(uid: string): string {
  return String(hashUid(uid) % 10000).padStart(4, "0");
}

/** mulberry32: a small deterministic PRNG seeded from the uid hash, so a uid always yields the same icon across clients. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return (): number => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic avatar for a uid, rendered at `sizePx` device pixels: a near-white
 * background nudged toward `tint` (the player's room/selection color, so the fill
 * matches the outline frame) under a music glyph whose hue is seeded from the uid.
 * Callers pass the displayed size × devicePixelRatio so the canvas is 1:1 with the
 * screen and nothing is blurred by downscaling. The glyph shape + hue are fixed by
 * the uid; only the background tint follows the room color.
 */
export function defaultAvatarDataUrl(uid: string, tint?: string, sizePx: number = DEFAULT_SIZE): string {
  const r = rng(hashUid(uid));
  const hue = Math.floor(r() * 360);
  const sat = 60 + Math.floor(r() * 16);
  const light = 38 + Math.floor(r() * 8);
  const glyph = GLYPHS[Math.floor(r() * GLYPHS.length)];

  const canvas = document.createElement("canvas");
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    return "";
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, sizePx, sizePx);
  if (tint !== undefined) {
    ctx.globalAlpha = BG_TINT_ALPHA;
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, sizePx, sizePx);
    ctx.globalAlpha = 1;
  }
  ctx.font = `${Math.round(sizePx * GLYPH_FRAC)}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const cx = sizePx / 2;
  const cy = sizePx * GLYPH_CY_FRAC;
  ctx.fillStyle = `hsl(${hue}, ${sat}%, ${light}%)`;
  ctx.fillText(glyph, cx, cy);
  return canvas.toDataURL("image/png");
}

const memo = new Map<string, string>();

/** Memoized {@link defaultAvatarDataUrl}: the data URL is generated once per (uid, tint, sizePx) and reused, so presence churn never re-runs the canvas draw. */
export function avatarFor(uid: string, tint?: string, sizePx: number = DEFAULT_SIZE): string {
  const key = `${uid}|${tint ?? ""}|${sizePx}`;
  let src = memo.get(key);
  if (src === undefined) {
    src = defaultAvatarDataUrl(uid, tint, sizePx);
    memo.set(key, src);
  }
  return src;
}
